/**
 * Voice AI — Guest Grid
 * Searchable card grid with stats and skeleton loading.
 * Dispatches: select-guest, search, page-change
 */

import { LitElement, html } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { guestStyles } from '../../styles/guest-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { guestService } from '../../services/guest-service.js';

export class GuestGrid extends LitElement {
  static properties = {
    guests: { type: Array },
    loading: { type: Boolean },
    searchQuery: { type: String },
    total: { type: Number },
    page: { type: Number },
  };

  static styles = [designSystem, componentStyles, guestStyles];

  constructor() {
    super();
    this.guests = [];
    this.loading = true;
    this.searchQuery = '';
    this.total = 0;
    this.page = 1;
    this._searchDebounce = null;
  }

  _onSearchInput(e) {
    const val = e.target.value;
    clearTimeout(this._searchDebounce);
    this._searchDebounce = setTimeout(() => {
      this.dispatchEvent(new CustomEvent('search', { detail: { query: val } }));
    }, 300);
  }

  _selectGuest(guest) {
    this.dispatchEvent(new CustomEvent('select-guest', { detail: { phone: guest.phone_number } }));
  }

  _getInitials(guest) {
    const name = guest.display_name || guest.first_name || '';
    if (!name) return '?';
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name[0].toUpperCase();
  }

  render() {
    return html`
      ${iconStyles}
      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">Guest Memory</h1>
          <p class="page-description">Guest profiles and conversation history</p>
        </div>
      </div>

      <div class="search-filters">
        <div class="search-input-wrap">
          <i class="fa-sharp fa-light fa-magnifying-glass"></i>
          <input
            type="text"
            class="search-input"
            placeholder="Search by name, phone, email..."
            .value=${this.searchQuery}
            @input=${this._onSearchInput}
          />
        </div>
        ${!this.loading ? html`<span class="results-count">${this.total} guest${this.total !== 1 ? 's' : ''}</span>` : ''}
      </div>

      ${this.loading ? html`
        <div class="guests-grid">
          ${[1, 2, 3].map(() => html`
            <div class="skeleton-card">
              <div class="skeleton-line" style="width:60%;height:20px;margin-bottom:12px;"></div>
              <div class="skeleton-line" style="width:40%;height:14px;margin-bottom:20px;"></div>
              <div class="skeleton-line" style="width:100%;height:14px;margin-bottom:8px;"></div>
              <div class="skeleton-line" style="width:80%;height:14px;"></div>
            </div>
          `)}
        </div>
      ` : this.guests.length === 0 ? html`
        <div class="empty-state" style="padding:var(--space-16) var(--space-8);text-align:center;">
          <div style="font-size:3rem;margin-bottom:var(--space-4);opacity:0.3;">
            <i class="fa-sharp fa-light fa-id-card"></i>
          </div>
          <div style="font-size:var(--text-lg);font-weight:var(--font-semibold);color:var(--color-text-primary);margin-bottom:var(--space-2);">
            ${this.searchQuery ? 'No matching guests' : 'No guest records yet'}
          </div>
          <div style="font-size:var(--text-sm);color:var(--color-text-tertiary);">
            ${this.searchQuery
              ? 'Try adjusting your search.'
              : 'Guest records are created automatically when callers interact with the Voice AI system.'}
          </div>
        </div>
      ` : html`
        <div class="guests-grid">
          ${this.guests.map(guest => this._renderCard(guest))}
        </div>
      `}
    `;
  }

  _renderCard(guest) {
    const name = guest.display_name || guestService.formatPhone(guest.phone_number);
    const tags = Array.isArray(guest.tags) ? guest.tags : [];
    return html`
      <div class="guest-card" @click=${() => this._selectGuest(guest)}>
        <div class="guest-card-header">
          <div class="guest-avatar">${this._getInitials(guest)}</div>
          <div class="guest-info">
            <div class="guest-name">${name}</div>
            <div class="guest-phone">${guestService.formatPhone(guest.phone_number)}</div>
          </div>
        </div>

        <div class="guest-stats-row">
          <div class="guest-stat-item">
            <span class="stat-value">${guest.call_count || 0}</span>
            <span class="stat-label">Calls</span>
          </div>
          <div class="guest-stat-item">
            <span class="stat-value">${guest.memory_count || 0}</span>
            <span class="stat-label">Memories</span>
          </div>
        </div>

        <div class="guest-card-footer">
          <span>Last call: ${guestService.relativeTime(guest.last_call_at)}</span>
          ${tags.length > 0 ? html`
            <div class="guest-tags">
              ${tags.slice(0, 2).map(t => html`<span class="guest-tag">${t}</span>`)}
            </div>
          ` : ''}
        </div>
      </div>
    `;
  }
}

customElements.define('guest-grid', GuestGrid);
